import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Wand2, X, Loader2, FileText, Undo2, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, notifyErr } from "@/lib/api";
import { PdfView } from "@/components/PdfView";

const inEditor = (el) => el.closest("[data-editor-ui]");
const SOLID = "bg-[#000] text-[#fff] hover:bg-[#222] disabled:opacity-60";
const LINE = "bg-transparent border border-[#000]/60 text-[#000] hover:bg-[#000]/10 disabled:opacity-50";
const FIELD = "bg-[#fff] text-[#000] placeholder:text-[#666] border-[#000]/30 focus-visible:ring-[#000]";
const PANEL = "bg-[#11A9A2] text-[#000] border border-[#000]/20";
const preview = (rules) => window.dispatchEvent(new CustomEvent("nolium-layout-preview", { detail: rules }));

function groupOf(tid) {
  const parts = tid.split("-");
  const levels = [];
  for (let n = parts.length - 1; n >= 1; n--) {
    const pre = `${parts.slice(0, n).join("-")}-`;
    const c = document.querySelectorAll(`[data-testid^="${pre}"]`).length;
    if (c > 1) levels.push(`${pre} (${c} elementi)`);
  }
  return levels.length ? { gruppi_prefisso: levels.join("; ") } : {};
}

function describe(el) {
  const anc = [];
  let p = el.parentElement;
  while (p && anc.length < 3) { const t = p.closest("[data-testid]"); if (!t) break; anc.push(t.dataset.testid); p = t.parentElement; }
  const figli = [...el.children].slice(0, 4).map((c) => `${c.tagName.toLowerCase()}: ${(c.innerText || "").trim().slice(0, 30) || "(grafica)"}`).join("; ");
  return { testid: el.dataset.testid, tag: el.tagName.toLowerCase(), text: (el.innerText || "").trim().slice(0, 160), antenati: anc.join(", "), figli, ...groupOf(el.dataset.testid) };
}

function useHoverPick(on, onPick) {
  const [box, setBox] = useState(null);
  useEffect(() => {
    if (!on) { setBox(null); return undefined; }
    const target = (e) => !inEditor(e.target) && e.target.closest?.("[data-testid]");
    const move = (e) => { const t = target(e); setBox(t ? t.getBoundingClientRect() : null); };
    const click = (e) => { const t = target(e); if (!t) return; e.preventDefault(); e.stopPropagation(); onPick(t); };
    document.addEventListener("mousemove", move, true);
    document.addEventListener("click", click, true);
    return () => { document.removeEventListener("mousemove", move, true); document.removeEventListener("click", click, true); };
  }, [on, onPick]);
  return box;
}

function ProposalBox({ prop, busy, onApply, onDiscard }) {
  if (!prop) return null;
  return (
    <div className="rounded-lg border border-[#000]/30 bg-[#fff]/40 text-[#000] p-3 space-y-2" data-testid="editor-proposal">
      <div className="text-sm font-medium" data-testid="editor-proposal-summary">{prop.summary}</div>
      <div className="text-xs text-[#000]/70">Anteprima applicata. Confermi la modifica per questa società?</div>
      <div className="flex gap-2">
        <Button size="sm" onClick={onApply} disabled={busy} className={SOLID} data-testid="editor-apply-btn">Conferma</Button>
        <Button size="sm" onClick={onDiscard} disabled={busy} className={LINE} data-testid="editor-discard-btn">Scarta</Button>
      </div>
    </div>
  );
}

function ContractEditor({ cid, open, onClose }) {
  const [info, setInfo] = useState(null);
  const [block, setBlock] = useState("");
  const [req, setReq] = useState("");
  const [prop, setProp] = useState(null);
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/editor/${cid}`).then((r) => setInfo(r.data)).catch(notifyErr);
  const render = async (contract) => {
    const r = await api.post(`/editor/${cid}/contract-preview`, { contract }, { responseType: "blob" });
    setUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { load(); } }, [open, cid]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && info) { render(prop?.contract || info.contract).catch(notifyErr); } }, [open, info, prop]);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { notifyErr(e); } finally { setBusy(false); } };
  const ask = () => run(async () => {
    const r = await api.post(`/editor/${cid}/propose`, { target: "contract", request: req, element: block ? { blocco: block } : {} });
    setProp(r.data);
  });
  const apply = () => run(async () => { await api.post(`/editor/${cid}/apply`, { target: "contract", summary: prop.summary, contract: prop.contract }); toast.success("Contratto aggiornato per questa società"); setProp(null); setReq(""); load(); });
  const undo = () => run(async () => { await api.post(`/editor/${cid}/undo`, { target: "contract" }); toast.success("Ultima modifica annullata"); setProp(null); load(); });
  const chips = info ? [...Object.entries(info.labels), ...Object.entries(info.blocks)] : [];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`max-w-6xl h-[90vh] flex flex-col ${PANEL} [&>button]:text-[#000]`} data-editor-ui data-testid="contract-editor">
        <DialogHeader>
          <DialogTitle className="text-[#000]">Editor contratto PDF</DialogTitle>
          <DialogDescription className="text-[#000]/75">Scegli una parte del contratto, descrivi la modifica e conferma dopo l'anteprima. Vale solo per questa società.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 grid md:grid-cols-[340px_1fr] gap-4 overflow-auto md:overflow-hidden">
          <div className="space-y-3 md:overflow-y-auto pr-1">
            <div className="flex flex-wrap gap-1.5" data-testid="contract-editor-blocks">
              {chips.map(([k, v]) => (
                <button key={k} onClick={() => setBlock(block === k ? "" : k)} data-testid={`contract-block-${k}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${block === k ? "bg-[#000] text-[#fff] border-[#000]" : "border-[#000]/40 text-[#000] hover:bg-[#000]/10"}`}>{v}</button>
              ))}
            </div>
            <Textarea rows={4} value={req} onChange={(e) => setReq(e.target.value)} placeholder="Es. logo più grande, titolo tabella blu, nascondi la riga km" className={FIELD} data-testid="contract-editor-request" />
            <Button onClick={ask} disabled={busy || !req.trim()} className={`w-full ${SOLID}`} data-testid="contract-editor-propose">
              {busy ? <Loader2 size={15} className="animate-spin mr-1" /> : <Wand2 size={15} className="mr-1" />}Proponi modifica</Button>
            <ProposalBox prop={prop} busy={busy} onApply={apply} onDiscard={() => setProp(null)} />
            <Button size="sm" onClick={undo} disabled={busy || !info?.history?.some((h) => h.target === "contract")} className={LINE} data-testid="contract-editor-undo"><Undo2 size={14} className="mr-1" />Annulla modifica</Button>
            <div className="space-y-1" data-testid="contract-editor-history">
              {(info?.history || []).filter((h) => h.target === "contract").slice(0, 8).map((h) => <div key={h.id} className="text-xs text-[#000]/70">• {h.summary}</div>)}
            </div>
          </div>
          <div className="min-h-[60vh] md:min-h-0 bg-zinc-950 rounded-md p-2 overflow-auto" data-testid="contract-editor-preview">
            {url ? <PdfView url={url} /> : <div className="text-sm text-zinc-500 p-4">Caricamento anteprima…</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ElementPanel({ cid, el, onClose, onDone }) {
  const [req, setReq] = useState("");
  const [prop, setProp] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { notifyErr(e); } finally { setBusy(false); } };
  const ask = () => run(async () => {
    const r = await api.post(`/editor/${cid}/propose`, { target: "app", request: req, element: el, viewport: window.innerWidth < 768 ? "mobile" : "desktop" });
    setProp(r.data); preview(r.data.rules);
  });
  const discard = () => { setProp(null); preview(null); };
  const apply = () => run(async () => {
    await api.post(`/editor/${cid}/apply`, { target: "app", summary: prop.summary, rules: prop.rules });
    toast.success("Modifica applicata per questa società"); preview(null); window.dispatchEvent(new Event("nolium-layout-refresh")); onDone();
  });
  return (
    <div data-editor-ui className={`fixed z-[60] bottom-20 inset-x-3 md:inset-x-auto md:right-6 md:w-[380px] rounded-xl p-4 space-y-3 shadow-2xl ${PANEL}`} data-testid="editor-element-panel">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-[#000]/70">Elemento selezionato</div>
          <div className="text-sm font-medium truncate" data-testid="editor-element-name">{el.text || el.testid}</div>
          <div className="text-[11px] text-[#000]/60 truncate">{el.testid}</div>
        </div>
        <button onClick={() => { discard(); onClose(); }} className="h-8 w-8 grid place-items-center rounded-md text-[#000] hover:bg-[#000]/10" data-testid="editor-element-close"><X size={16} /></button>
      </div>
      <Textarea rows={3} value={req} onChange={(e) => setReq(e.target.value)} placeholder="Descrivi la modifica (es. testo più grande in grassetto, nascondi su cellulare)" className={FIELD} data-testid="editor-request-input" />
      <Button onClick={ask} disabled={busy || !req.trim()} className={`w-full ${SOLID}`} data-testid="editor-propose-btn">
        {busy ? <Loader2 size={15} className="animate-spin mr-1" /> : <Wand2 size={15} className="mr-1" />}Proponi modifica</Button>
      <ProposalBox prop={prop} busy={busy} onApply={apply} onDiscard={discard} />
    </div>
  );
}

export function InternalEditor({ cid }) {
  const [on, setOn] = useState(false);
  const [el, setEl] = useState(null);
  const [pdf, setPdf] = useState(false);
  const pick = useRef((t) => setEl(describe(t))).current;
  const box = useHoverPick(on && !pdf, pick);
  const undo = async () => {
    try { await api.post(`/editor/${cid}/undo`, { target: "app" }); toast.success("Ultima modifica annullata"); window.dispatchEvent(new Event("nolium-layout-refresh")); } catch (e) { notifyErr(e); }
  };
  const exit = () => { setOn(false); setEl(null); preview(null); };
  return (
    <>
      <Button size="sm" onClick={() => (on ? exit() : setOn(true))} className={`h-10 bg-[#11A9A2] hover:bg-[#0e8f89] text-[#000] ${on ? "ring-2 ring-[#000]/60" : ""}`} data-editor-ui data-testid="internal-editor-toggle">
        <Wand2 size={15} className="sm:mr-1" /><span className="hidden sm:inline">Editor interno</span></Button>
      {createPortal(<>
      {on && box && <div className="fixed z-[55] pointer-events-none rounded-md border-2 border-[#11A9A2] bg-[#11A9A2]/15 transition-all duration-75" style={{ top: box.top, left: box.left, width: box.width, height: box.height }} />}
      {on && !pdf && (
        <div data-editor-ui className={`fixed z-[60] bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full px-3 py-2 shadow-2xl max-w-[96vw] overflow-x-auto ${PANEL}`} data-testid="editor-toolbar">
          <span className="text-xs text-[#000]/80 font-medium hidden sm:flex items-center gap-1 whitespace-nowrap"><MousePointerClick size={14} />Clicca un elemento</span>
          <Button size="sm" onClick={() => setPdf(true)} className={LINE} data-testid="editor-open-contract"><FileText size={14} className="sm:mr-1" /><span className="hidden sm:inline">Contratto PDF</span></Button>
          <Button size="sm" onClick={undo} className={LINE} data-testid="editor-undo-btn"><Undo2 size={14} className="sm:mr-1" /><span className="hidden sm:inline">Annulla modifica</span></Button>
          <Button size="sm" onClick={exit} className={SOLID} data-testid="editor-exit-btn"><X size={14} className="mr-1" />Esci</Button>
        </div>
      )}
      {on && el && <ElementPanel key={el.testid} cid={cid} el={el} onClose={() => setEl(null)} onDone={() => setEl(null)} />}
      </>, document.body)}
      <ContractEditor cid={cid} open={pdf} onClose={() => setPdf(false)} />
    </>
  );
}
